import { useUserStore } from '../store/userStore';
import type { FunctionalRole } from '../types';

/**
 * Single source of truth for "which persona's dashboard/experience is the
 * user currently viewing". Replaces four previously-divergent local
 * computations (RoleDashboard, AppShell/SidebarContent, AdaptiveView,
 * PMDashboard) that disagreed on precedence, validation, and fallback.
 *
 * Precedence:
 *   1. `activeRole`        — the user's explicit, ephemeral "view as" choice
 *                             (see userStore), honoured ONLY while it is
 *                             still one of the user's actual functional
 *                             roles. A stale selection (e.g. an admin
 *                             revoked that role) is silently discarded
 *                             rather than shown.
 *   2. `primaryRole`        — the account's persistent, server-set default
 *                             role (Settings > My Profile), honoured ONLY
 *                             under the same validation as `activeRole` — a
 *                             revoked functional role must not keep winning
 *                             here just because it used to be primary.
 *   3. `functionalRoles[0]` — first role granted via JWT claim / Firestore
 *                             profile, for accounts that never set a
 *                             primary role explicitly.
 *   4. `_DEFAULT_ROLE`      — terminal fallback when the caller has no
 *                             functional roles at all. There used to be a
 *                             `sessionConfig.role` layer here, but
 *                             post-migration `/session/config` returns
 *                             `entity_role` ('admin' | 'member'), so that
 *                             layer always matched and always resolved to
 *                             'member' — never a real persona, and
 *                             `['today-briefing', 'member']` was a dead
 *                             query key. `'viewer'` was tried as the
 *                             terminal instead, but `RoleDashboard` mapped
 *                             it to `<ExecutiveDashboard>`, so the "most
 *                             conservative fallback" actually rendered the
 *                             most privileged one. `_DEFAULT_ROLE` matches
 *                             the backend's own fallback (see
 *                             `routers/briefing._resolve_persona`) and is a
 *                             real `FunctionalRole` that `RoleDashboard`
 *                             already renders correctly via its `default`
 *                             case (`<PMDashboard>`) — no dispatch fix
 *                             needed for it, unlike `'viewer'`.
 *
 * `activeRole` is a VIEW MODE, not an account property: selecting a persona
 * to look at must never mutate `primaryRole` server-side. See
 * `userStore.setActiveRole` vs `setUser`.
 */
const _DEFAULT_ROLE: FunctionalRole = 'product_manager';

export function useEffectiveRole(): FunctionalRole {
  const { activeRole, currentUser, sessionConfig } = useUserStore();

  // Merge JWT claims with the Firestore profile so admin-assigned role
  // changes are reflected immediately, without waiting for a token refresh.
  const functionalRoles: FunctionalRole[] =
    currentUser?.functionalRoles?.length
      ? currentUser.functionalRoles
      : sessionConfig?.functionalRoles ?? [];

  const primaryRole = currentUser?.primaryRole;

  return (
    (activeRole && functionalRoles.includes(activeRole) ? activeRole : null) ??
    (primaryRole && functionalRoles.includes(primaryRole) ? primaryRole : null) ??
    functionalRoles[0] ??
    _DEFAULT_ROLE
  );
}
